import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';

export type FleetOwnershipType = 'OWNED' | 'FREELANCE' | 'RENTAL' | 'TRANSPORTER';
export type FleetResourceType = 'DRIVER' | 'VEHICLE';

export interface FleetOwnershipTransferResource {
    resourceType: FleetResourceType;
    resourceId: number;
}

export interface FleetOwnershipTransferRequest {
    owningCompanyId: number;
    targetCompanyId?: number | null;
    resources: FleetOwnershipTransferResource[];
    targetOwnershipType: FleetOwnershipType;
    reason: string;
    rentalAgreementId?: number | null;
    transporterCrmClientId?: number | null;
}

export interface FleetOwnershipEligibility {
    resources: Array<FleetOwnershipTransferResource & { currentOwnershipType: FleetOwnershipType }>;
}

export interface FleetOwnershipTransferResult {
    resources: Array<FleetOwnershipTransferResource & {
        previousOwnershipType: FleetOwnershipType;
        ownershipType: FleetOwnershipType;
        outcome: 'TRANSFERRED' | 'UNCHANGED';
    }>;
}

export interface FleetOwnershipTransferAudit {
    fleetOwnershipTransferId: number;
    owningCompanyId: number;
    targetCompanyId?: number | null;
    resourceType: FleetResourceType;
    resourceId: number;
    sourceOwnershipType: FleetOwnershipType;
    targetOwnershipType: FleetOwnershipType;
    rentalAgreementId?: number | null;
    transporterCrmClientId?: number | null;
    reason: string;
    outcome: string;
    createdAt?: string;
    createdBy?: number | null;
}

@Injectable({ providedIn: 'root' })
export class FleetOwnershipTransferService {
    private readonly baseUrl = `${environment.restApiUrl}fleet/ownership-transfers`;

    constructor(private http: HttpClient) { }

    destinations(owningCompanyId: number): Observable<Array<{ id: number; name: string }>> {
        return this.http.get<Array<{ id: number; name: string }>>(`${this.baseUrl}/destinations`, {
            params: new HttpParams().set('owningCompanyId', owningCompanyId)
        });
    }

    checkEligibility(owningCompanyId: number, resources: FleetOwnershipTransferResource[], targetCompanyId?: number | null): Observable<APIResponseWrapper<FleetOwnershipEligibility>> {
        return this.http.post<APIResponseWrapper<FleetOwnershipEligibility>>(`${this.baseUrl}/eligibility`, {
            owningCompanyId,
            targetCompanyId,
            resources: resources.map(({ resourceType, resourceId }) => ({ resourceType, resourceId }))
        });
    }

    transfer(request: FleetOwnershipTransferRequest): Observable<APIResponseWrapper<FleetOwnershipTransferResult>> {
        return this.http.post<APIResponseWrapper<FleetOwnershipTransferResult>>(this.baseUrl, {
            ...request,
            resources: request.resources.map(({ resourceType, resourceId }) => ({ resourceType, resourceId }))
        });
    }

    history(owningCompanyId: number, resourceType: FleetResourceType, resourceId: number): Observable<FleetOwnershipTransferAudit[]> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId)
            .set('resourceType', resourceType)
            .set('resourceId', resourceId);
        return this.http.get<FleetOwnershipTransferAudit[]>(`${this.baseUrl}/history`, { params });
    }
}
